import { z } from 'zod';
import { Estimate } from './estimate.js';
/**
 * Ước lượng story point cho một việc đang gõ trong form — APM-AIA-a.
 *
 * Story point là **khối lượng việc** (độ lớn, độ phức tạp, độ chưa chắc chắn),
 * không phải thời gian của một người cụ thể — người dùng chốt 2026-09-25. Vì
 * vậy phép ước lượng chỉ đọc nội dung việc và những việc tương tự đã xong;
 * tốc độ của người làm chỉ vào cuộc ở bước quy điểm ra giờ (APM-AIA-b/c).
 */
/**
 * Thang điểm — Fibonacci, như mọi đội Scrum quen dùng.
 *
 * Khoảng cách tăng dần là có chủ đích: việc càng to thì sai số càng lớn, và
 * một thang cho phép nói "7 điểm" là một thang giả vờ đo chính xác thứ không
 * đo chính xác được.
 */
export const STORY_POINT_SCALE = [1, 2, 3, 5, 8, 13, 21];
/** Đưa một số bất kỳ về thang: giá trị **nhỏ nhất ≥ n**, trần là 21. */
export function toStoryPointScale(value) {
    const found = STORY_POINT_SCALE.find((point) => point >= value);
    return found ?? STORY_POINT_SCALE[STORY_POINT_SCALE.length - 1];
}
/** Mô tả phải dài **hơn** bấy nhiêu từ mới đủ căn cứ để AI ước lượng. */
export const DETAIL_MIN_WORDS = 50;
/**
 * Số từ của một đoạn văn — bỏ thẻ HTML (mô tả đến từ trình soạn thảo giàu định
 * dạng) và chỉ đếm cụm có chữ hoặc số, để một dãy gạch đầu dòng hay dấu câu
 * không được tính là "từ".
 */
export function countWords(text) {
    const plain = text
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;|&#160;/g, ' ')
        .replace(/&[a-z]+;|&#\d+;/gi, ' ');
    return plain.split(/\s+/).filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
}
/**
 * Việc đã đủ chi tiết để dùng tính năng AI chưa — có checklist, **hoặc** mô tả
 * trên `DETAIL_MIN_WORDS` từ.
 *
 * Một hàm, hai chỗ gọi: form gọi trước khi gửi (để nói ngay mà không tốn một
 * lượt mạng), server gọi lại (vì form không phải cửa duy nhất vào API). Hai
 * bản chép tay của cùng một ngưỡng sẽ lệch nhau ở lần đầu ai đó đổi con số.
 */
export function isDetailedEnough(input) {
    const hasChecklist = (input.checklist ?? []).some((item) => item.trim().length > 0);
    return hasChecklist || countWords(input.description ?? '') > DETAIL_MIN_WORDS;
}
export const StoryPointEstimateInput = z.object({
    title: z.string().trim().min(3).max(500),
    description: z.string().max(20_000).optional(),
    /** Chữ của từng mục checklist — đủ để đo khối lượng, không cần trọng số. */
    checklist: z.array(z.string().max(500)).max(100).optional(),
    runId: z.uuid().optional(),
});
/**
 * Kết quả model nộp — một `Estimate` (§0.4 của `plan-ai-features.md`) với đơn
 * vị cố định là điểm.
 *
 * `unit` là literal chứ không phải cả `EstimateUnit`: một ước lượng "8 giờ"
 * lọt vào ô story point là một con số đúng định dạng và sai nghĩa hoàn toàn.
 */
export const StoryPointSuggestion = Estimate.extend({ unit: z.literal('sp') });
export const StoryPointEstimate = z.object({
    /** `null` khi không có căn cứ nào — không AI, không việc tương tự đã xong. */
    estimate: Estimate.nullable(),
    /**
     * `ai` — model đọc checklist/mô tả và việc tương tự. `similar` — trung vị
     * điểm của việc tương tự đã xong (chạy cả khi chưa có key AI).
     */
    source: z.enum(['ai', 'similar']),
    /** Vì sao không phải `ai` — `no_key`, `failed`, hoặc `null` khi là `ai`. */
    aiSkipped: z.enum(['no_key', 'failed']).nullable(),
});
//# sourceMappingURL=story-point-estimate.js.map